export type TimeZoneOption = {
  value: string;
  label: string;
};

const normalizeUtcOffsetLabel = (label: string): string =>
  label.replace("GMT", "UTC");

export const getCurrentBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_error) {
    return "UTC";
  }
};

export const getSupportedTimeZones = (): string[] => {
  const intlWithSupportedValues = Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  };
  const values = intlWithSupportedValues.supportedValuesOf?.("timeZone") ?? [];
  return values.length > 0 ? values : [];
};

export const getTimeZoneOffsetLabel = (timeZone: string): string => {
  const offsetStyles: Array<"longOffset" | "shortOffset"> = [
    "longOffset",
    "shortOffset",
  ];
  for (const style of offsetStyles) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZoneName: style,
      }).formatToParts(new Date());
      const timeZoneName = parts.find((part) => part.type === "timeZoneName");
      if (timeZoneName?.value) {
        return normalizeUtcOffsetLabel(timeZoneName.value);
      }
    } catch (_error) {
      // try next style
    }
  }
  return "UTC";
};

export const getTimeZoneOffsetMinutes = (timeZone: string): number => {
  const offsetLabel = getTimeZoneOffsetLabel(timeZone);
  const match = offsetLabel.match(/UTC([+-])(\d{1,2})(?::?(\d{2}))?/);
  if (!match) return 0;
  const sign = match[1] === "-" ? -1 : 1;
  const hours = Number.parseInt(match[2], 10);
  const minutes = match[3] ? Number.parseInt(match[3], 10) : 0;
  return sign * (hours * 60 + minutes);
};

export const formatTimeZoneOptionLabel = (timeZone: string): string =>
  `${timeZone} (${getTimeZoneOffsetLabel(timeZone)})`;

export const buildSortedTimeZoneOptions = (
  selectedTimeZone: string,
): TimeZoneOption[] => {
  const supported = getSupportedTimeZones();
  const baseList =
    supported.length > 0
      ? supported
      : Array.from(new Set([selectedTimeZone, "UTC"]));
  const normalized = baseList.includes(selectedTimeZone)
    ? baseList
    : [selectedTimeZone, ...baseList];

  const sorted = [...normalized].sort((a, b) => {
    const offsetDiff =
      getTimeZoneOffsetMinutes(a) - getTimeZoneOffsetMinutes(b);
    if (offsetDiff !== 0) return offsetDiff;
    return a.localeCompare(b);
  });

  return sorted.map((timeZone) => ({
    value: timeZone,
    label: formatTimeZoneOptionLabel(timeZone),
  }));
};
